import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import CloseIcon from '@mui/icons-material/Close';
import SpeedIcon from '@mui/icons-material/Speed';
import { usePlaybackStore } from '../../stores/playback.store';
import { useSelectionStore } from '../../stores/selection.store';

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2] as const;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function MiniPlayer() {
  const {
    isPlaying,
    playlist,
    currentIndex,
    volume,
    rate,
    currentParagraphId,
    ambientUrl,
    ambientVolume,
    togglePlayPause,
    next,
    previous,
    setVolume,
    setRate,
    onTrackEnded,
    stop,
  } = usePlaybackStore();

  const select = useSelectionStore((s) => s.select);
  const audioRef = useRef<HTMLAudioElement>(null);
  const ambientRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const currentItem = currentIndex >= 0 ? playlist[currentIndex] : null;
  const audioUrl = currentItem?.audioUrl;

  // Sync play/pause state with audio element.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying && audioUrl) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [isPlaying, audioUrl]);

  // Update volume and rate.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = rate;
    }
  }, [volume, rate]);

  // Sync ambient audio play/pause and volume.
  useEffect(() => {
    const ambient = ambientRef.current;
    if (!ambient) return;
    if (isPlaying && ambientUrl) {
      ambient.play().catch(() => {});
    } else {
      ambient.pause();
    }
  }, [isPlaying, ambientUrl]);

  useEffect(() => {
    if (ambientRef.current) {
      ambientRef.current.volume = ambientVolume;
    }
  }, [ambientVolume]);

  // Sync paragraph selection with current playing paragraph.
  useEffect(() => {
    if (currentParagraphId && isPlaying) {
      select(currentParagraphId);
      const node = document.querySelector(
        `[data-paragraph-id="${currentParagraphId}"]`,
      );
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentParagraphId, isPlaying, select]);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setDuration(audio.duration);
      setCurrentTime(0);
    }
  }, []);

  const handleSeek = (_: unknown, value: number | number[]) => {
    const audio = audioRef.current;
    if (audio && typeof value === 'number') {
      audio.currentTime = value;
      setCurrentTime(value);
    }
  };

  const cycleSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(rate as (typeof SPEED_OPTIONS)[number]);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length]!;
    setRate(next);
  };

  const toggleMute = () => {
    setVolume(volume > 0 ? 0 : 1);
  };

  if (playlist.length === 0) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        px: 3,
        py: 1,
      }}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={audioUrl ?? undefined}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={onTrackEnded}
      />
      {ambientUrl && <audio ref={ambientRef} src={ambientUrl} loop />}

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          cursor: 'pointer',
        }}
      />

      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Transport controls */}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            size="small"
            onClick={previous}
            disabled={currentIndex <= 0}
          >
            <SkipPreviousIcon />
          </IconButton>
          <IconButton size="small" onClick={togglePlayPause}>
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={next}
            disabled={currentIndex >= playlist.length - 1}
          >
            <SkipNextIcon />
          </IconButton>
        </Stack>

        {/* Time display */}
        <Typography
          variant="caption"
          sx={{ minWidth: 80, textAlign: 'center' }}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>

        {/* Seek bar */}
        <Slider
          size="small"
          value={currentTime}
          max={duration || 1}
          onChange={handleSeek}
          sx={{ flex: 1, mx: 2 }}
        />

        {/* Speaker label */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {currentItem?.speakerLabel ?? `Paragraph ${currentIndex + 1}`}
        </Typography>

        {/* Track counter */}
        <Typography variant="caption" color="text.secondary">
          {currentIndex + 1}/{playlist.length}
        </Typography>

        {/* Speed control */}
        <Tooltip title="Playback speed">
          <IconButton size="small" onClick={cycleSpeed}>
            <SpeedIcon sx={{ fontSize: 18 }} />
            <Typography
              variant="caption"
              sx={{ ml: 0.25, fontSize: 10, fontWeight: 600 }}
            >
              {rate}x
            </Typography>
          </IconButton>
        </Tooltip>

        {/* Volume */}
        <IconButton size="small" onClick={toggleMute}>
          {volume > 0 ? (
            <VolumeUpIcon fontSize="small" />
          ) : (
            <VolumeOffIcon fontSize="small" />
          )}
        </IconButton>

        {/* Close */}
        <IconButton size="small" onClick={stop}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
